import { supabaseService } from './supabase-service.js';

class CalendarImportService {
  /**
   * Parse and import events from an iCal file or URL
   * @param {string} icalContent - The iCal file content or URL
   * @param {Object} options - Import options
   * @returns {Promise<Object>} Import results
   */
  async importFromIcal(icalContent, options = {}) {
    try {
      const {
        source = 'ical',
        calendarName = 'Imported Calendar',
        category = 'external',
        familyId = 'single-family'
      } = options;

      let content;
      
      // Handle URL vs file content
      if (icalContent.startsWith('http')) {
        const response = await fetch(icalContent);
        content = await response.text();
      } else {
        content = icalContent;
      }

      // Parse iCal content using simple regex patterns
      const events = this.parseICalContent(content);
      
      const importResults = {
        total: events.length,
        imported: 0,
        skipped: 0,
        errors: []
      };

      for (const event of events) {
        try {
          // Check if event already exists
          const existingEvent = await this.findExistingEvent(source, event.uid);
          if (existingEvent && !options.allowDuplicates) {
            importResults.skipped++;
            continue;
          }

          // Map external event to internal format
          const internalEvent = this.mapIcalEventToInternal(event, {
            source,
            calendarName,
            category,
            familyId
          });

          // Import the event
          await supabaseService.addCalendarEvent(internalEvent);
          importResults.imported++;

        } catch (eventError) {
          console.error('Error importing individual event:', eventError);
          importResults.errors.push({
            event: event?.summary || 'Unknown event',
            error: eventError.message
          });
        }
      }

      return importResults;

    } catch (error) {
      console.error('Error importing iCal:', error);
      throw new Error(`Failed to import calendar: ${error.message}`);
    }
  }

  /**
   * Simple iCal parser using regex patterns
   * @param {string} content - Raw iCal content
   * @returns {Array} Parsed events
   */
  parseICalContent(content) {
    const events = [];
    
    // Split content into VEVENT blocks
    const eventBlocks = content.split('BEGIN:VEVENT');
    
    for (let i = 1; i < eventBlocks.length; i++) {
      const eventBlock = eventBlocks[i];
      const endIndex = eventBlock.indexOf('END:VEVENT');
      
      if (endIndex === -1) continue;
      
      const eventContent = eventBlock.substring(0, endIndex);
      const event = this.parseEventBlock(eventContent);
      
      if (event.summary || event.dtstart) {
        events.push(event);
      }
    }
    
    return events;
  }

  /**
   * Parse individual event block
   * @param {string} eventContent - Event content block
   * @returns {Object} Parsed event object
   */
  parseEventBlock(eventContent) {
    const event = {};
    const lines = eventContent.split(/\r?\n/);
    
    for (let line of lines) {
      line = line.trim();
      if (!line) continue;
      
      // Handle line continuations (lines starting with space or tab)
      let fullLine = line;
      let nextLineIndex = lines.indexOf(line) + 1;
      while (nextLineIndex < lines.length && /^\s/.test(lines[nextLineIndex])) {
        fullLine += lines[nextLineIndex].substring(1);
        nextLineIndex++;
      }
      
      // Parse field:value pairs
      const colonIndex = fullLine.indexOf(':');
      if (colonIndex === -1) continue;
      
      const field = fullLine.substring(0, colonIndex).toUpperCase();
      const value = fullLine.substring(colonIndex + 1);
      
      // Extract field name (remove parameters)
      const fieldName = field.split(';')[0];
      
      switch (fieldName) {
        case 'SUMMARY':
          event.summary = this.unescapeText(value);
          break;
        case 'DESCRIPTION':
          event.description = this.unescapeText(value);
          break;
        case 'LOCATION':
          event.location = this.unescapeText(value);
          break;
        case 'DTSTART':
          event.dtstart = this.parseDateTime(value, field);
          break;
        case 'DTEND':
          event.dtend = this.parseDateTime(value, field);
          break;
        case 'UID':
          event.uid = value;
          break;
        case 'STATUS':
          event.status = value;
          break;
      }
    }
    
    return event;
  }

  /**
   * Parse iCal datetime string
   * @param {string} dateString - iCal datetime string
   * @param {string} field - Full field with parameters
   * @returns {Date|null} Parsed date
   */
  parseDateTime(dateString, field) {
    try {
      // Check if it's a date-only field (VALUE=DATE)
      const isDateOnly = field.includes('VALUE=DATE') || !/T\d{6}/.test(dateString);
      
      if (isDateOnly) {
        // Date only: YYYYMMDD
        const year = parseInt(dateString.substring(0, 4));
        const month = parseInt(dateString.substring(4, 6)) - 1; // Month is 0-based
        const day = parseInt(dateString.substring(6, 8));
        return { date: new Date(year, month, day), isDateOnly: true };
      } else {
        // DateTime: YYYYMMDDTHHMMSS or YYYYMMDDTHHMMSSZ
        const year = parseInt(dateString.substring(0, 4));
        const month = parseInt(dateString.substring(4, 6)) - 1;
        const day = parseInt(dateString.substring(6, 8));
        const hour = parseInt(dateString.substring(9, 11));
        const minute = parseInt(dateString.substring(11, 13));
        const second = parseInt(dateString.substring(13, 15)) || 0;
        
        return { date: new Date(year, month, day, hour, minute, second), isDateOnly: false };
      }
    } catch (error) {
      console.error('Error parsing datetime:', dateString, error);
      return null;
    }
  }

  /**
   * Unescape iCal text content
   * @param {string} text - Escaped text
   * @returns {string} Unescaped text
   */
  unescapeText(text) {
    return text
      .replace(/\\n/g, '\n')
      .replace(/\\,/g, ',')
      .replace(/\\;/g, ';')
      .replace(/\\\\/g, '\\');
  }

  /**
   * Map iCal event to internal calendar event format
   */
  mapIcalEventToInternal(event, options) {
    const { source, calendarName, category, familyId } = options;
    
    // Handle start date/time
    const startDateTime = event.dtstart;
    const endDateTime = event.dtend;
    
    if (!startDateTime || !startDateTime.date) {
      throw new Error('Event missing start date');
    }
    
    const startDate = startDateTime.date;
    const endDate = endDateTime ? endDateTime.date : null;
    const isAllDay = startDateTime.isDateOnly;

    return {
      family_id: familyId,
      title: event.summary || 'Untitled Event',
      description: event.description || '',
      date: startDate.toISOString().split('T')[0], // YYYY-MM-DD format
      time: isAllDay ? null : startDate.toTimeString().substring(0, 5), // HH:MM format
      end_date: endDate ? endDate.toISOString().split('T')[0] : null,
      end_time: (endDate && !isAllDay) ? endDate.toTimeString().substring(0, 5) : null,
      type: category,
      location: event.location || null,
      
      // External calendar metadata
      external_source: source,
      external_id: event.uid || `${source}-${Date.now()}-${Math.random()}`,
      external_calendar_id: calendarName,
      sync_enabled: true,
      import_date: new Date().toISOString(),
      
      // Default values for family calendar system
      activities: [],
      meal_plan: null,
      rsvp_enabled: false,
      rsvps: {}
    };
  }

  /**
   * Check if an external event already exists
   */
  async findExistingEvent(source, externalId) {
    try {
      const { data, error } = await supabaseService.supabase
        .from('calendar_events')
        .select('id')
        .eq('external_source', source)  
        .eq('external_id', externalId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error checking for existing event:', error);
      }

      return data;
    } catch (error) {
      console.error('Error in findExistingEvent:', error);
      return null;
    }
  }

  /**
   * Sync external calendars that have sync_enabled = true
   */
  async syncExternalCalendars() {
    try {
      // Get all external calendar sources that need syncing
      const { data: externalSources, error } = await supabaseService.supabase
        .from('calendar_events')
        .select('external_source, external_calendar_id')
        .eq('sync_enabled', true)
        .not('external_source', 'is', null);

      if (error) {
        console.error('Error fetching external sources:', error);
        return;
      }

      // Group by source and calendar ID for efficient syncing
      const sourcesMap = new Map();
      externalSources.forEach(source => {
        const key = `${source.external_source}:${source.external_calendar_id}`;
        if (!sourcesMap.has(key)) {
          sourcesMap.set(key, source);
        }
      });

      // Sync each unique external calendar
      for (const source of sourcesMap.values()) {
        try {
          await this.syncSingleExternalCalendar(source);
        } catch (syncError) {
          console.error(`Error syncing ${source.external_source}:${source.external_calendar_id}:`, syncError);
        }
      }

    } catch (error) {
      console.error('Error in syncExternalCalendars:', error);
    }
  }

  /**
   * Sync a single external calendar source
   */
  async syncSingleExternalCalendar(source) {
    // This would be implemented based on the specific external calendar type
    // For now, we'll focus on the import functionality
    console.log(`Syncing ${source.external_source} calendar: ${source.external_calendar_id}`);
    
    // Update last_sync timestamp
    await supabaseService.supabase
      .from('calendar_events')
      .update({ last_sync: new Date().toISOString() })
      .eq('external_source', source.external_source)
      .eq('external_calendar_id', source.external_calendar_id);
  }

  /**
   * Remove all events from a specific external calendar
   */
  async removeExternalCalendar(source, calendarId) {
    try {
      const { error } = await supabaseService.supabase
        .from('calendar_events')
        .delete()
        .eq('external_source', source)
        .eq('external_calendar_id', calendarId);

      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error) {
      console.error('Error removing external calendar:', error);
      throw new Error(`Failed to remove external calendar: ${error.message}`);
    }
  }

  /**
   * Get import statistics
   */
  async getImportStats() {
    try {
      const { data, error } = await supabaseService.supabase
        .from('calendar_events')
        .select('external_source, external_calendar_id, count(*)')
        .not('external_source', 'is', null)
        .group('external_source, external_calendar_id');

      if (error) {
        console.error('Error fetching import stats:', error);
        return [];
      }

      return data;
    } catch (error) {
      console.error('Error in getImportStats:', error);
      return [];
    }
  }
}

export const calendarImportService = new CalendarImportService();
export default calendarImportService;